/**
 * Audit Entry Card Component
 * 
 * Displays a single audit entry with expandable details
 */

import { useState } from 'react';
import type { AuditEntry, AuditChange } from '@/types/audit';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  ChevronDown, 
  ChevronUp,
  PencilLine,
  UserCheck,
  Undo2,
  SkipForward,
  Zap
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { cn } from '@/lib/utils';

interface AuditEntryCardProps {
  entry: AuditEntry;
}

const actionLabels: Record<string, string> = {
  created: 'Created',
  saved_draft: 'Saved Draft',
  modified: 'Modified',
  submitted: 'Submitted',
  approved: 'Approved',
  rejected: 'Rejected',
  resubmitted: 'Resubmitted',
  recalled: 'Recalled',
  deleted: 'Deleted',
  locked: 'Locked',
  unlocked: 'Unlocked',
  comment_added: 'Comment Added',
  approver_edited: 'Edited by Approver',
  stage_skipped: 'Stage Skipped',
  rule_triggered: 'Rule Triggered',
};

// Map action types to badge colors using Tailwind classes
const actionBadgeColors: Record<string, string> = {
  created: 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-200 dark:border-blue-800',
  saved_draft: 'bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-800/30 dark:text-gray-200 dark:border-gray-700',
  modified: 'bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-200 dark:border-yellow-800',
  submitted: 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-200 dark:border-blue-800',
  approved: 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/30 dark:text-green-200 dark:border-green-800',
  rejected: 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-200 dark:border-red-800',
  resubmitted: 'bg-purple-100 text-purple-800 border-purple-200 dark:bg-purple-900/30 dark:text-purple-200 dark:border-purple-800',
  recalled: 'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-200 dark:border-amber-800',
  deleted: 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-200 dark:border-red-800',
  locked: 'bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-900/30 dark:text-orange-200 dark:border-orange-800',
  unlocked: 'bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-900/30 dark:text-orange-200 dark:border-orange-800',
  comment_added: 'bg-purple-100 text-purple-800 border-purple-200 dark:bg-purple-900/30 dark:text-purple-200 dark:border-purple-800',
  approver_edited: 'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-200 dark:border-amber-800',
  stage_skipped: 'bg-slate-100 text-slate-800 border-slate-200 dark:bg-slate-800/30 dark:text-slate-200 dark:border-slate-700',
  rule_triggered: 'bg-indigo-100 text-indigo-800 border-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-200 dark:border-indigo-800',
};

/** Turns a raw entry object { projectCode, projectName, taskCode, description }
 *  into a readable one-liner, e.g. "Project Alpha (P-001) · Task: DEV-1 · Notes: fixing bug" */
function formatEntryObject(obj: any): string {
  if (!obj || typeof obj !== 'object') return String(obj ?? 'None');

  const parts: string[] = [];

  if (obj.projectName || obj.projectCode) {
    const name = obj.projectName || '';
    const code = obj.projectCode || '';
    parts.push(name && code ? `${name} (${code})` : name || code);
  }

  if (obj.taskCode) parts.push(`Task: ${obj.taskCode}`);

  if (obj.description && obj.description.trim()) {
    parts.push(`Notes: ${obj.description}`);
  }

  return parts.length > 0 ? parts.join(' · ') : 'New row';
}

/** Formats a plain scalar value for display */
function formatScalar(value: any): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'number') {
    // Hours values — show as "8 hrs" or "0 hrs"
    return `${value} hr${value === 1 ? '' : 's'}`;
  }
  return String(value);
}

function ChangeDetail({ change }: { change: AuditChange }) {
  const getFieldLabel = (field: string) => {
    const labels: Record<string, string> = {
      entry_added:   'Row Added',
      entry_removed: 'Row Removed',
      hours_monday:    'Monday Hours',
      hours_tuesday:   'Tuesday Hours',
      hours_wednesday: 'Wednesday Hours',
      hours_thursday:  'Thursday Hours',
      hours_friday:    'Friday Hours',
      hours_saturday:  'Saturday Hours',
      hours_sunday:    'Sunday Hours',
      // legacy short-form keys kept for safety
      hours_mon: 'Monday Hours',
      hours_tue: 'Tuesday Hours',
      hours_wed: 'Wednesday Hours',
      hours_thu: 'Thursday Hours',
      hours_fri: 'Friday Hours',
      hours_sat: 'Saturday Hours',
      hours_sun: 'Sunday Hours',
      description:  'Description',
      project:      'Project',
      cost_centre:  'Cost Centre',
      task:         'Task',
    };
    return labels[field] || field;
  };

  const isEntryAdded   = change.field === 'entry_added';
  const isEntryRemoved = change.field === 'entry_removed';
  const isEntryChange  = isEntryAdded || isEntryRemoved;

  return (
    <div className="text-xs space-y-1 p-2 bg-muted/50 rounded">
      <div className="font-medium text-muted-foreground">{getFieldLabel(change.field)}</div>

      {isEntryAdded && (
        <div className="flex items-start gap-1 text-green-700 dark:text-green-400">
          <span className="font-bold mt-px">+</span>
          <span>{formatEntryObject(change.newValue)}</span>
        </div>
      )}

      {isEntryRemoved && (
        <div className="flex items-start gap-1 text-red-600 dark:text-red-400">
          <span className="font-bold mt-px">−</span>
          <span>{formatEntryObject(change.oldValue)}</span>
        </div>
      )}

      {!isEntryChange && (
        <div className="space-y-0.5">
          <div className="flex items-center gap-1 text-red-600 dark:text-red-400">
            <span className="font-bold">−</span>
            <span>{formatScalar(change.oldValue)}</span>
          </div>
          <div className="flex items-center gap-1 text-green-700 dark:text-green-400">
            <span className="font-bold">+</span>
            <span>{formatScalar(change.newValue)}</span>
          </div>
        </div>
      )}
    </div>
  );
}

export function AuditEntryCard({ entry }: AuditEntryCardProps) {
  const [expanded, setExpanded] = useState(false);
  const hasDetails = entry.changes && entry.changes.length > 0;
  
  // Special styling for rejection and approver-edited entries
  const isRejection = entry.actionType === 'rejected';
  const isRecall = entry.actionType === 'recalled';
  const isApproverEdit = entry.actionType === 'approver_edited';
  const isStageSkipped = entry.actionType === 'stage_skipped';
  const isRuleTriggered = entry.actionType === 'rule_triggered';
  const originalApproverName = entry.delegatedFromManagerName || entry.onBehalfOfApproverName;
  const isDelegatedAction =
    (entry.actionType === 'approved' || entry.actionType === 'rejected') &&
    (entry.isDelegated === true || !!entry.onBehalfOfApproverName) &&
    !!originalApproverName;
  const cardClassName = isRejection
    ? 'mb-3 border-red-200 dark:border-red-900 bg-red-50/50 dark:bg-red-950/20'
    : isRecall
    ? 'mb-3 border-amber-200 dark:border-amber-900 bg-amber-50/50 dark:bg-amber-950/20'
    : isApproverEdit
    ? 'mb-3 border-amber-200 dark:border-amber-900 bg-amber-50/50 dark:bg-amber-950/20'
    : isDelegatedAction
    ? 'mb-3 border-blue-200 dark:border-blue-900 bg-blue-50/30 dark:bg-blue-950/10'
    : isStageSkipped
    ? 'mb-3 border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-900/20'
    : isRuleTriggered
    ? 'mb-3 border-indigo-200 dark:border-indigo-900 bg-indigo-50/50 dark:bg-indigo-950/20'
    : 'mb-3';

  return (
    <Card className={cardClassName}>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          {/* Content */}
          <div className="flex-1 min-w-0">
            {/* Header */}
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge 
                    variant="outline"
                    className={cn(
                      "pointer-events-none",
                      actionBadgeColors[entry.actionType] || 'bg-gray-100 text-gray-800 border-gray-200'
                    )}
                  >
                    {actionLabels[entry.actionType] || entry.actionType}
                  </Badge>
                  <span className="text-sm font-medium">{entry.actorName}</span>
                  {entry.actorRole && (
                    <span className="text-xs text-muted-foreground">
                      ({entry.actorRole})
                    </span>
                  )}
                </div>
                
                {/* Status change */}
                {entry.previousStatus && entry.newStatus && (
                  <div className="text-xs text-muted-foreground mt-1">
                    Status: <span className="font-medium">{entry.previousStatus}</span>
                    {' → '}
                    <span className="font-medium">{entry.newStatus}</span>
                  </div>
                )}
              </div>

              {/* Timestamp */}
              <div className="text-xs text-muted-foreground text-right flex-shrink-0">
                <div>{formatDistanceToNow(entry.timestamp, { addSuffix: true })}</div>
                <div className="text-[10px]">
                  {format(entry.timestamp, 'MMM d, yyyy h:mm a')}
                </div>
              </div>
            </div>

            {/* Approver Edit indicator */}
            {isApproverEdit && (
              <div className="flex items-center gap-1.5 text-xs text-amber-700 dark:text-amber-400 mb-2">
                <PencilLine className="h-3.5 w-3.5" />
                <span>Timesheet entries were edited by this approver during review</span>
              </div>
            )}

            {/* Stage skipped indicator */}
            {isStageSkipped && (
              <div className="flex items-center gap-1.5 text-xs text-slate-700 dark:text-slate-300 mb-2 bg-slate-50 dark:bg-slate-900/30 border border-slate-200 dark:border-slate-700 rounded px-2 py-1">
                <SkipForward className="h-3.5 w-3.5 flex-shrink-0" />
                <span>{entry.comment}</span>
              </div>
            )}

            {/* Rule triggered indicator */}
            {isRuleTriggered && (
              <div className="flex items-center gap-1.5 text-xs text-indigo-700 dark:text-indigo-300 mb-2 bg-indigo-50 dark:bg-indigo-950/30 border border-indigo-200 dark:border-indigo-800 rounded px-2 py-1">
                <Zap className="h-3.5 w-3.5 flex-shrink-0" />
                <span>{entry.comment}</span>
              </div>
            )}

            {/* Recall indicator */}
            {isRecall && (
              <div className="flex items-center gap-1.5 text-xs text-amber-700 dark:text-amber-400 mb-2 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded px-2 py-1">
                <Undo2 className="h-3.5 w-3.5 flex-shrink-0" />
                <span>Timesheet was recalled from approval — returned to draft for editing</span>
              </div>
            )}

            {/* Delegation indicator — "on behalf of" label */}
            {isDelegatedAction && (
              <div className="flex items-center gap-1.5 text-xs text-blue-700 dark:text-blue-300 mb-2 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded px-2 py-1">
                <UserCheck className="h-3.5 w-3.5 flex-shrink-0" />
                <span>
                  {entry.actionType === 'approved' ? 'Approved' : 'Rejected'} by{' '}
                  <span className="font-semibold">{entry.actorName}</span>
                  {' '}on behalf of{' '}
                  <span className="font-semibold">{originalApproverName}</span>
                </span>
              </div>
            )}

            {/* Rejection Reason - Prominently displayed for rejections */}
            {isRejection && entry.comment && (
              <div className="text-sm bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-800 p-3 rounded mb-2">
                <div className="flex items-start gap-2">
                  <span className="font-semibold text-red-900 dark:text-red-100">Rejection Reason:</span>
                </div>
                <div className="mt-1 text-red-800 dark:text-red-200">{entry.comment}</div>
              </div>
            )}

            {/* Comment (for non-rejection entries) */}
            {!isRejection && entry.comment && (
              <div className="text-sm bg-muted/50 p-2 rounded mb-2">
                <span className="font-medium">Comment: </span>
                {entry.comment}
              </div>
            )}

            {/* Changes summary */}
            {entry.changes && entry.changes.length > 0 && !expanded && (
              <div className="text-xs text-muted-foreground">
                {entry.changes.length} change{entry.changes.length !== 1 ? 's' : ''}
              </div>
            )}

            {/* Expanded details */}
            {expanded && entry.changes && entry.changes.length > 0 && (
              <div className="mt-2 space-y-2">
                {entry.changes.map((change, index) => (
                  <ChangeDetail key={index} change={change} />
                ))}
              </div>
            )}

            {/* Expand/collapse button */}
            {hasDetails && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setExpanded(!expanded)}
                className="mt-2 h-7 text-xs"
              >
                {expanded ? (
                  <>
                    <ChevronUp className="h-3 w-3 mr-1" />
                    Hide Details
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-3 w-3 mr-1" />
                    View Details
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
